import type { EmployeeProfile, FinanceRole } from '@/api/finance';

/**
 * 财务系统的权限位与信息架构（纯函数，无 React/DOM 依赖，便于单测）。
 *
 * 权限来自 `GET /admin-api/ext/finance/me`：后端按**员工档案的岗位**
 * （employee / accountant / cashier）派生权限位，超级管理员（wildcard）拥有全部。
 * 没有建档（或已离职）的后台账号 `can_login=false`，整站阻断。
 *
 * 直接敲 URL 访问无权限页面时必须 fail closed —— canAccessFinancePath 会把地址
 * 归到最具体的菜单入口，再回到同一份权限判断。
 */

export const FINANCE_PERMISSIONS = {
  /** 填报本人报销单（所有在职员工都有）。 */
  claimSubmit: 'finance.claim.submit',
  /** 查看财务数据。 */
  read: 'finance.read',
  /** 会计审核报销单。 */
  claimReview: 'finance.claim.review',
  /** 出纳复核 + 登记打款。 */
  claimPay: 'finance.claim.pay',
  /** 应收应付、供应商账单勾稽决策、会计代录报销。 */
  ledgerManage: 'finance.ledger.manage',
  /** 资金账户、收支流水登记 / 入账 / 冲销。 */
  cashManage: 'finance.cash.manage',
  /** CSV 导出。 */
  export: 'finance.export',
  /** 员工建档 / 改岗 / 离职 / 查看完整证件号（只有超管）。 */
  employeeManage: 'finance.employee.manage',
  /** 查看财务审计日志（只有超管；后端 SUPER_ONLY，不属于任何岗位）。 */
  auditRead: 'finance.audit.read',
} as const;

export type FinancePermission = (typeof FINANCE_PERMISSIONS)[keyof typeof FINANCE_PERMISSIONS];

/** 岗位 → 权限位，与 admin `extensions/finance/access.ts` 的 FINANCE_ROLE_PERMISSIONS 一致。 */
export const FINANCE_ROLE_PERMISSIONS: Record<FinanceRole, readonly FinancePermission[]> = {
  employee: [FINANCE_PERMISSIONS.claimSubmit],
  accountant: [
    FINANCE_PERMISSIONS.claimSubmit,
    FINANCE_PERMISSIONS.read,
    FINANCE_PERMISSIONS.claimReview,
    FINANCE_PERMISSIONS.ledgerManage,
    FINANCE_PERMISSIONS.export,
  ],
  cashier: [
    FINANCE_PERMISSIONS.claimSubmit,
    FINANCE_PERMISSIONS.read,
    FINANCE_PERMISSIONS.claimPay,
    FINANCE_PERMISSIONS.cashManage,
  ],
};

export const FINANCE_ROLE_LABELS: Record<FinanceRole, string> = {
  employee: '员工',
  accountant: '会计',
  cashier: '出纳',
};

export function financeRoleLabel(role: string | null | undefined): string {
  if (!role) return '未分配';
  return FINANCE_ROLE_LABELS[role as FinanceRole] ?? role;
}

/** 后端授予的权限集合。超管 wildcard=true，拥有一切权限位。 */
export interface PermissionGrant {
  wildcard: boolean;
  permissions: readonly string[];
}

/** `/ext/finance/me` 的完整身份信封。 */
export interface FinanceIdentity extends PermissionGrant {
  admin: { id: number; username?: string | null; email?: string | null; role?: string } | null;
  /** 有在职员工档案。 */
  enrolled: boolean;
  /** 能不能进财务系统（超管或在职员工）。false 时整站阻断。 */
  can_login: boolean;
  finance_role: FinanceRole | null;
  employee: EmployeeProfile | null;
}

export const EMPTY_IDENTITY: FinanceIdentity = {
  admin: null,
  wildcard: false,
  enrolled: false,
  can_login: false,
  finance_role: null,
  employee: null,
  permissions: [],
};

export interface FinanceMenuItem {
  key: string;
  label: string;
  href: string;
  /** 条目级权限位；省略时继承整组的 permission。 */
  permission?: string;
}

export interface FinanceMenuGroup {
  key: string;
  label: string;
  /** 整组的默认准入权限位；条目可以用自己的 permission 覆盖。 */
  permission?: string;
  items: readonly FinanceMenuItem[];
}

/**
 * 条目真正的准入权限位：条目自己的优先，其次继承组。两边都没有时返回 null，
 * 调用方一律按「不放行」处理（fail closed），绝不因为漏配就变成人人可见。
 */
export function menuItemPermission(
  group: Pick<FinanceMenuGroup, 'permission'>,
  item: Pick<FinanceMenuItem, 'permission'>,
): string | null {
  return item.permission ?? group.permission ?? null;
}

function itemAllowed(
  grant: PermissionGrant | null | undefined,
  group: FinanceMenuGroup,
  item: FinanceMenuItem,
): boolean {
  const permission = menuItemPermission(group, item);
  if (permission === null) return false;
  return can(grant, permission);
}

/** 财务系统的全量菜单（与 router.tsx 一一对应）。 */
export const FINANCE_MENU: readonly FinanceMenuGroup[] = [
  {
    key: 'mine',
    label: '我的报销',
    permission: FINANCE_PERMISSIONS.claimSubmit,
    items: [
      { key: 'my-claims', label: '我的报销单', href: '/my/claims' },
      { key: 'my-claims-new', label: '新建报销单', href: '/my/claims/new' },
      { key: 'my-profile', label: '我的档案', href: '/my/profile' },
    ],
  },
  {
    key: 'review',
    label: '报销审核',
    permission: FINANCE_PERMISSIONS.claimReview,
    items: [{ key: 'review', label: '会计审核台', href: '/review' }],
  },
  {
    key: 'cashier',
    label: '出纳工作台',
    permission: FINANCE_PERMISSIONS.claimPay,
    items: [{ key: 'cashier', label: '出纳工作台', href: '/cashier' }],
  },
  {
    key: 'finance',
    label: '财务管理',
    permission: FINANCE_PERMISSIONS.read,
    items: [
      { key: 'overview', label: '财务总览', href: '/overview' },
      { key: 'claims', label: '报销管理', href: '/claims' },
      { key: 'transactions', label: '收支流水', href: '/transactions' },
      { key: 'obligations', label: '应收应付', href: '/obligations' },
      { key: 'accounts', label: '资金账户', href: '/accounts' },
      { key: 'categories', label: '费用分类', href: '/categories' },
      { key: 'supplier-statements', label: '供应商账单勾稽', href: '/supplier-statements' },
      { key: 'reports', label: '月度报表', href: '/reports' },
    ],
  },
  {
    // 系统管理：员工档案 / 部门管理（超管建档、改岗、维护组织结构）与审计日志
    // （超管查全量操作留痕）。每个条目各有各的权限位，组本身不设 permission ——
    // 任一条目可见就显示这一组。
    key: 'system',
    label: '系统管理',
    items: [
      {
        key: 'employees',
        label: '员工档案',
        href: '/employees',
        permission: FINANCE_PERMISSIONS.employeeManage,
      },
      {
        key: 'departments',
        label: '部门管理',
        href: '/departments',
        // 部门列表本身每个在职员工都读得到，但维护组织结构只有超管。
        permission: FINANCE_PERMISSIONS.employeeManage,
      },
      {
        key: 'audit',
        label: '审计日志',
        href: '/audit',
        permission: FINANCE_PERMISSIONS.auditRead,
      },
    ],
  },
];

/** 权限判定的唯一口径：wildcard（超管）或显式持有该权限位。 */
export function can(grant: PermissionGrant | null | undefined, permission: string): boolean {
  if (!grant) return false;
  if (grant.wildcard) return true;
  return grant.permissions.includes(permission);
}

/**
 * 按权限过滤出可见的菜单：先逐条目判权限（条目没配就继承组），
 * 一个条目都留不下的分组整组不显示。
 */
export function buildFinanceMenu(grant: PermissionGrant | null | undefined): FinanceMenuGroup[] {
  return FINANCE_MENU.map((group) => ({
    ...group,
    items: group.items.filter((item) => itemAllowed(grant, group, item)),
  })).filter((group) => group.items.length > 0);
}

/** 落地顺序：我的报销 → 会计审核台 → 出纳工作台 → 财务总览。 */
const LANDING_ORDER: readonly { permission: string; href: string }[] = [
  { permission: FINANCE_PERMISSIONS.claimSubmit, href: '/my/claims' },
  { permission: FINANCE_PERMISSIONS.claimReview, href: '/review' },
  { permission: FINANCE_PERMISSIONS.claimPay, href: '/cashier' },
  { permission: FINANCE_PERMISSIONS.read, href: '/overview' },
];

/** 当前授权下的首页；一个入口都没有时返回 null。 */
export function defaultFinancePath(grant: PermissionGrant | null | undefined): string | null {
  return LANDING_ORDER.find((entry) => can(grant, entry.permission))?.href ?? null;
}

/** 财务系统是否对当前账号完全关闭（一个菜单入口都没有）。 */
export function hasAnyFinanceAccess(grant: PermissionGrant | null | undefined): boolean {
  if (defaultFinancePath(grant) !== null) return true;
  // 落地页覆盖不到的入口（员工档案 / 审计日志，理论上只有超管）也算有入口。
  return buildFinanceMenu(grant).length > 0;
}

function matchesRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

/**
 * 把浏览器地址归到最具体的菜单条目：
 * `/my/claims/12/edit` → `/my/claims`，`/claims/7` → `/claims`。
 * 返回条目本身（而不是整组），这样同一组里权限不同的条目也能各判各的。
 */
export function resolveFinanceMenuPath(
  pathname: string,
): { group: FinanceMenuGroup; item: FinanceMenuItem } | undefined {
  let best: { group: FinanceMenuGroup; item: FinanceMenuItem; length: number } | undefined;
  for (const group of FINANCE_MENU) {
    for (const item of group.items) {
      if (!matchesRoute(pathname, item.href)) continue;
      if (!best || item.href.length > best.length) {
        best = { group, item, length: item.href.length };
      }
    }
  }
  return best ? { group: best.group, item: best.item } : undefined;
}

/**
 * 直接访问某个地址是否放行。未知地址（例如 `/`，由路由自己重定向）不拦，
 * 已知地址一律回到权限位判断——没有匹配到权限就是 403。
 */
export function canAccessFinancePath(
  pathname: string,
  grant: PermissionGrant | null | undefined,
): boolean {
  const hit = resolveFinanceMenuPath(pathname);
  if (!hit) return true;
  return itemAllowed(grant, hit.group, hit.item);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * 解析 `GET /ext/finance/me`。结构不兼容时返回 null（页面按加载失败处理，
 * 绝不退化成「放行」）。
 */
export function parseFinanceIdentity(value: unknown): FinanceIdentity | null {
  const record = asRecord(value);
  if (!record) return null;
  if (typeof record.wildcard !== 'boolean') return null;
  if (typeof record.can_login !== 'boolean') return null;
  if (!isStringArray(record.permissions)) return null;
  const admin = asRecord(record.admin);
  const role =
    typeof record.finance_role === 'string' ? (record.finance_role as FinanceRole) : null;
  return {
    admin: admin
      ? {
          id: Number(admin.id),
          username: (admin.username as string | null) ?? null,
          email: (admin.email as string | null) ?? null,
          role: typeof admin.role === 'string' ? admin.role : undefined,
        }
      : null,
    wildcard: record.wildcard,
    enrolled: record.enrolled === true,
    can_login: record.can_login,
    finance_role: role,
    employee: (asRecord(record.employee) as EmployeeProfile | null) ?? null,
    permissions: record.permissions,
  };
}
